import { MoodEntry, MoodPattern, MonthlyReport } from '../types'

export function analyzeMoodPatterns(entries: MoodEntry[]): MoodPattern {
  if (entries.length === 0) {
    return {
      bestDayOfWeek: 0,
      worstDayOfWeek: 0,
      bestDayAverage: 0,
      worstDayAverage: 0,
      weeklyPattern: [],
      trend: 'stable',
      consistency: 0
    }
  }

  // Analyze by day of week
  const dayStats: Record<number, { total: number; count: number }> = {}
  
  entries.forEach(entry => {
    const dayOfWeek = new Date(entry.date).getDay()
    if (!dayStats[dayOfWeek]) {
      dayStats[dayOfWeek] = { total: 0, count: 0 }
    }
    dayStats[dayOfWeek].total += entry.mood
    dayStats[dayOfWeek].count += 1
  })

  const weeklyPattern = Array.from({ length: 7 }, (_, day) => ({
    day,
    average: dayStats[day] ? Number((dayStats[day].total / dayStats[day].count).toFixed(1)) : 0,
    count: dayStats[day]?.count || 0
  }))

  const validDays = weeklyPattern.filter(d => d.count > 0)
  const bestDay = validDays.reduce((best, current) => 
    current.average > best.average ? current : best
  )
  const worstDay = validDays.reduce((worst, current) => 
    current.average < worst.average ? current : worst
  )

  // Calculate trend
  const recentEntries = entries.slice(-14) // Last 14 entries
  const olderEntries = entries.slice(-28, -14) // Previous 14 entries
  
  const recentAvg = recentEntries.reduce((sum, e) => sum + e.mood, 0) / recentEntries.length
  const olderAvg = olderEntries.length > 0 ? 
    olderEntries.reduce((sum, e) => sum + e.mood, 0) / olderEntries.length : recentAvg

  let trend: 'improving' | 'declining' | 'stable' = 'stable'
  if (recentAvg > olderAvg + 0.3) trend = 'improving'
  else if (recentAvg < olderAvg - 0.3) trend = 'declining'

  // Calculate consistency (standard deviation)
  const moods = entries.map(e => e.mood)
  const avg = moods.reduce((sum, mood) => sum + mood, 0) / moods.length
  const variance = moods.reduce((sum, mood) => sum + Math.pow(mood - avg, 2), 0) / moods.length
  const consistency = Math.max(0, 1 - Math.sqrt(variance) / 2) // Normalize to 0-1

  return {
    bestDayOfWeek: bestDay.day,
    worstDayOfWeek: worstDay.day,
    bestDayAverage: bestDay.average,
    worstDayAverage: worstDay.average,
    weeklyPattern,
    trend,
    consistency: Number(consistency.toFixed(2))
  }
}

export function generateMonthlyReport(entries: MoodEntry[], month: number, year: number): MonthlyReport {
  const monthEntries = entries.filter(entry => {
    const date = new Date(entry.date)
    return date.getMonth() === month && date.getFullYear() === year
  })

  if (monthEntries.length === 0) {
    return {
      month,
      year,
      totalEntries: 0,
      averageMood: 0,
      bestDay: '',
      worstDay: '',
      moodDistribution: {},
      streakDays: 0
    }
  }

  const totalMood = monthEntries.reduce((sum, entry) => sum + entry.mood, 0)
  const averageMood = Number((totalMood / monthEntries.length).toFixed(1))

  // Find best and worst days
  const sortedByMood = [...monthEntries].sort((a, b) => b.mood - a.mood)
  const bestDay = sortedByMood[0] ? new Date(sortedByMood[0].date).toLocaleDateString('it-IT') : ''
  const worstDay = sortedByMood[sortedByMood.length - 1] ? 
    new Date(sortedByMood[sortedByMood.length - 1].date).toLocaleDateString('it-IT') : ''

  // Mood distribution
  const moodDistribution: Record<number, number> = {}
  monthEntries.forEach(entry => {
    moodDistribution[entry.mood] = (moodDistribution[entry.mood] || 0) + 1
  })

  // Calculate streak for the month
  let streakDays = 0
  const sortedByDate = [...monthEntries].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  
  for (let i = 0; i < sortedByDate.length; i++) {
    if (i === 0) {
      streakDays = 1
    } else {
      const prevDate = new Date(sortedByDate[i - 1].date)
      const currDate = new Date(sortedByDate[i].date)
      const dayDiff = Math.floor((currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24))
      
      if (dayDiff === 1) {
        streakDays++
      } else {
        streakDays = 1
      }
    }
  }

  return {
    month,
    year,
    totalEntries: monthEntries.length,
    averageMood,
    bestDay,
    worstDay,
    moodDistribution,
    streakDays
  }
}